import { useEffect, useState } from "react";
import { KeyRound, Loader2, Mail, MapPin, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

export default function AdminAccountTab() {
    const { refresh } = useAuth();
    const [me, setMe] = useState(null);
    const [emailForm, setEmailForm] = useState({ email: "", current_password: "" });
    const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [contactForm, setContactForm] = useState({ phone: "", address: "" });
    const [busyEmail, setBusyEmail] = useState(false);
    const [busyPw, setBusyPw] = useState(false);
    const [busyContact, setBusyContact] = useState(false);

    useEffect(() => {
        api.get("/auth/me").then((r) => {
            setMe(r.data);
            setEmailForm((f) => ({ ...f, email: r.data.email || "" }));
            setContactForm({ phone: r.data.phone || "", address: r.data.address || "" });
        }).catch(() => {});
    }, []);

    const saveContact = async () => {
        setBusyContact(true);
        try {
            await api.put("/auth/profile", { name: me.name, phone: contactForm.phone, picture: me.picture || "", address: contactForm.address });
            await refresh();
            toast.success("Adres ve telefon bilgileriniz güncellendi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusyContact(false);
        }
    };

    const saveEmail = async () => {
        setBusyEmail(true);
        try {
            await api.put("/auth/profile/email", emailForm);
            await refresh();
            setEmailForm((f) => ({ ...f, current_password: "" }));
            toast.success("Giriş e-postanız güncellendi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncellenemedi");
        } finally {
            setBusyEmail(false);
        }
    };

    const savePassword = async () => {
        if (pwForm.new_password !== pwForm.confirm) return toast.error("Yeni şifreler eşleşmiyor");
        setBusyPw(true);
        try {
            await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
            setPwForm({ current_password: "", new_password: "", confirm: "" });
            toast.success("Şifreniz değiştirildi — diğer cihazlardaki oturumlar kapatıldı");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Değiştirilemedi");
        } finally {
            setBusyPw(false);
        }
    };

    if (!me) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;

    return (
        <div className="max-w-2xl space-y-6" data-testid="admin-account-tab">
            <div>
                <h2 className="font-display text-xl font-bold">Admin Bilgileri</h2>
                <p className="mt-1 text-sm text-muted-foreground">Yönetici hesabınızın giriş e-postasını ve şifresini buradan güncelleyin.</p>
            </div>

            <div className="space-y-4 rounded-2xl border bg-card p-6 shadow-sm">
                <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-terra-600" />
                    <h3 className="font-display font-bold">Giriş E-postası</h3>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yeni E-posta</label>
                    <Input type="email" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} data-testid="admin-email-input" />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mevcut Şifre (onay için) *</label>
                    <Input type="password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} data-testid="admin-email-password-input" />
                </div>
                <Button onClick={saveEmail} disabled={busyEmail || !emailForm.email.trim() || !emailForm.current_password} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="admin-email-save">
                    {busyEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    E-postayı Güncelle
                </Button>
            </div>

            <div className="space-y-4 rounded-2xl border bg-card p-6 shadow-sm">
                <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-terra-600" />
                    <h3 className="font-display font-bold">Adres & Telefon</h3>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Telefon</label>
                    <Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="05xx xxx xx xx" data-testid="admin-phone-input" />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Adres</label>
                    <Textarea rows={3} value={contactForm.address} onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })} placeholder="Mahalle, cadde, no, ilçe / il" data-testid="admin-address-input" />
                </div>
                <Button onClick={saveContact} disabled={busyContact} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="admin-contact-save">
                    {busyContact ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Adres & Telefonu Kaydet
                </Button>
            </div>

            <div className="space-y-4 rounded-2xl border bg-card p-6 shadow-sm">
                <div className="flex items-center gap-2">
                    <KeyRound className="h-5 w-5 text-terra-600" />
                    <h3 className="font-display font-bold">Şifre Değiştir</h3>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mevcut Şifre *</label>
                    <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="admin-pw-current" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yeni Şifre *</label>
                        <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="admin-pw-new" />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yeni Şifre (Tekrar) *</label>
                        <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="admin-pw-confirm" />
                    </div>
                </div>
                <Button onClick={savePassword} disabled={busyPw || !pwForm.current_password || pwForm.new_password.length < 8 || !pwForm.confirm} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="admin-pw-save">
                    {busyPw ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Şifreyi Değiştir
                </Button>
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    Şifre değişikliğinde bu cihaz hariç tüm oturumlar güvenlik için kapatılır.
                </p>
            </div>
        </div>
    );
}
